import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { useCoachPendingTranscriptReviewCount } from "@/hooks/useCoachPendingTranscriptReviewCount";

// Multi-tab note: localStorage last-writer-wins is accepted for v1.
const STORAGE_KEY_PREFIX = "coach-analysis-ready-seen-v1";
const EMPTY_PENDING_TRANSCRIPT_COUNTS: Record<string, number> = {};

function buildStorageKey(userId: string): string {
  return `${STORAGE_KEY_PREFIX}:${userId}`;
}

function hasStoredSnapshot(storage: Storage, key: string): boolean {
  try {
    return storage.getItem(key) !== null;
  } catch {
    return false;
  }
}

function readSnapshot(storage: Storage, key: string): Record<string, number> {
  try {
    const raw = storage.getItem(key);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, number>;
    }
  } catch {
    // corrupt storage — start fresh
  }
  return {};
}

function writeSnapshot(storage: Storage, key: string, counts: Record<string, number>): void {
  try {
    storage.setItem(key, JSON.stringify(counts));
  } catch {
    // storage unavailable — silently skip
  }
}

/**
 * Fires a sonner toast when one or more leaders' pending transcript review
 * count increases relative to the last-seen snapshot stored in localStorage.
 *
 * Seed-on-first-load: when no snapshot exists for a userId, the current
 * counts are written as the baseline and no toast is shown.
 *
 * Mount this hook in CoachLayout (not here — do not import/use it yet).
 */
export function useCoachAnalysisReadyToast(userId: string | undefined): void {
  const { pendingTranscriptCountByLeaderId, isLoadingPendingTranscriptReviewCount } =
    useCoachPendingTranscriptReviewCount();
  const navigate = useNavigate();
  const pendingTranscriptCounts = pendingTranscriptCountByLeaderId ?? EMPTY_PENDING_TRANSCRIPT_COUNTS;

  // Track the userId for which we have already seeded the snapshot. Storing
  // the userId (rather than a boolean) means we re-seed automatically when
  // the user switches accounts.
  const seededForUserRef = useRef<string | null>(null);

  useEffect(() => {
    if (!userId) return;
    // Guard: while the query is loading, counts are {}.  Never diff or seed
    // against the transient empty state — both paths produce false toasts.
    if (isLoadingPendingTranscriptReviewCount) return;
    if (typeof window === "undefined" || !window.localStorage) return;

    const storageKey = buildStorageKey(userId);
    const snapshot = readSnapshot(window.localStorage, storageKey);
    const hasSnapshot =
      hasStoredSnapshot(window.localStorage, storageKey) || seededForUserRef.current === userId;

    if (!hasSnapshot) {
      // First-ever load for this user — seed the snapshot and do not toast.
      seededForUserRef.current = userId;
      writeSnapshot(window.localStorage, storageKey, { ...pendingTranscriptCounts });
      return;
    }

    // Find leaders whose count increased.
    const increased: string[] = [];
    for (const [leaderId, count] of Object.entries(pendingTranscriptCounts)) {
      const prev = snapshot[leaderId] ?? 0;
      if (count > prev) {
        increased.push(leaderId);
      }
    }

    // Update snapshot for any change (increase or decrease), silently.
    const newSnapshot = { ...pendingTranscriptCounts };
    writeSnapshot(window.localStorage, storageKey, newSnapshot);

    if (increased.length === 0) return;

    const n = increased.length;
    const title = n === 1 ? "Session analysis ready" : `Analyses ready for ${n} leaders`;
    const description =
      "Veera finished analyzing a session. A 2-minute review keeps the record sharp.";

    toast.message(title, {
      description,
      duration: 5000,
      action: {
        label: "Open reviews",
        onClick: () => navigate("/coach/reviews"),
      },
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, pendingTranscriptCounts, isLoadingPendingTranscriptReviewCount]);
}
